<template>
  <div class="kb">
    <header class="kb-hero">
      <div class="kb-hero__orb kb-hero__orb--a" aria-hidden="true"></div>
      <div class="kb-hero__orb kb-hero__orb--b" aria-hidden="true"></div>
      <span class="kb-eyebrow">ALT GROUP · База знаний</span>
      <h1>Как работает система — простыми словами</h1>
      <p class="kb-level" v-if="levelTitle"><span class="kb-dot" :class="'kb-dot--' + level"></span>{{ levelTitle }}</p>

      <label class="kb-search" :class="{ 'kb-search--busy': searching }">
        <span class="kb-search__icon" aria-hidden="true">⌕</span>
        <input ref="input" v-model="query" type="search" :placeholder="placeholder" maxlength="200"
               @keydown.esc="query = ''" />
        <kbd v-if="!query">/</kbd>
      </label>
      <div class="kb-chips">
        <button v-for="ex in examples" :key="ex" class="kb-chip" @click="ask(ex)">{{ ex }}</button>
      </div>
    </header>

    <p v-if="error" class="kb-error">{{ error }}</p>

    <transition name="kb-fade" mode="out-in">
      <section v-if="query.trim()" key="search" class="kb-results">
        <article v-if="answer" class="kb-answer" @click="open(answer.slug)">
          <span class="kb-answer__label">Ответ</span>
          <p class="kb-answer__text">
            <template v-for="(part, i) in mark(answer.snippet)" :key="i"><mark v-if="part.hit">{{ part.text }}</mark><template v-else>{{ part.text }}</template></template>
          </p>
          <span class="kb-answer__more">{{ answer.title }} — читать полностью →</span>
        </article>
        <p v-if="!searching && !results.length" class="kb-empty">
          Ничего не нашли. Попробуйте другими словами — например, «отмена после 16», «ИП», «где рейс».
        </p>
        <transition-group name="kb-list" tag="div" class="kb-list">
          <button v-for="item in results" :key="item.slug" class="kb-item" @click="open(item.slug)">
            <span class="kb-item__title">{{ item.title }}</span>
            <span class="kb-item__text">
              <template v-for="(part, i) in mark(item.snippet || item.summary)" :key="i"><mark v-if="part.hit">{{ part.text }}</mark><template v-else>{{ part.text }}</template></template>
            </span>
            <span class="kb-item__meta">{{ sectionTitle(item.section) }}<em v-if="item.access !== 'all'"> · {{ item.access_title }}</em></span>
          </button>
        </transition-group>
      </section>

      <section v-else key="sections" class="kb-grid">
        <div v-if="loading && !sections.length" class="kb-skeleton">
          <div v-for="n in 6" :key="n" class="kb-skeleton__card"></div>
        </div>
        <article v-for="(section, si) in sections" :key="section.key" class="kb-section"
                 :style="{ animationDelay: (si * 60) + 'ms' }">
          <h2><span class="kb-section__icon">{{ section.icon }}</span>{{ section.title }}</h2>
          <button v-for="a in section.articles" :key="a.slug" class="kb-link" @click="open(a.slug)">
            <span>{{ a.title }}</span>
            <small>{{ a.summary }}</small>
            <i v-if="a.access !== 'all'" class="kb-badge" :class="'kb-badge--' + a.access">{{ a.access_title }}</i>
          </button>
        </article>
      </section>
    </transition>

    <transition name="kb-drawer">
      <div v-if="article || articleLoading" class="kb-overlay" @click.self="close">
        <aside class="kb-reader" role="dialog" aria-modal="true">
          <button class="kb-close" aria-label="Закрыть" @click="close">×</button>
          <p v-if="articleLoading" class="kb-muted">Открываем статью…</p>
          <template v-else-if="article">
            <span class="kb-reader__section">{{ sectionTitle(article.section) }}</span>
            <h2>{{ article.title }}</h2>
            <i v-if="article.access !== 'all'" class="kb-badge" :class="'kb-badge--' + article.access">{{ article.access_title }}</i>
            <p class="kb-reader__lead">{{ article.summary }}</p>
            <template v-for="(block, i) in blocks" :key="i">
              <p v-if="block.type === 'p'">{{ block.text }}</p>
              <ul v-else><li v-for="(item, j) in block.items" :key="j">{{ item }}</li></ul>
            </template>
            <div v-if="article.related && article.related.length" class="kb-related">
              <h3>Ещё в разделе</h3>
              <button v-for="r in article.related" :key="r.slug" class="kb-link kb-link--small" @click="open(r.slug)">
                <span>{{ r.title }}</span>
              </button>
            </div>
          </template>
        </aside>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchKnowledge, fetchArticle } from '@/backend/knowledgeApi'
import { parseBody, highlight, EXAMPLES, debounce } from '@/pages/knowledge/knowledge.mjs'

export default {
  name: 'KnowledgePage',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const input = ref(null)
    const sections = ref([])
    const level = ref('all')
    const levelTitle = ref('')
    const loading = ref(false)
    const error = ref('')
    const query = ref(String(route.query.q || ''))
    const results = ref([])
    const answer = ref(null)
    const searching = ref(false)
    const article = ref(null)
    const articleLoading = ref(false)
    const placeholderIndex = ref(0)
    let placeholderTimer = null

    const placeholder = computed(() => 'Спросите: ' + EXAMPLES[placeholderIndex.value])
    const examples = EXAMPLES.slice(0, 4)
    const blocks = computed(() => parseBody(article.value?.body))
    const titles = computed(() => Object.fromEntries(sections.value.map((s) => [s.key, s.title])))
    const sectionTitle = (key) => titles.value[key] || ''
    const mark = (text) => highlight(text, query.value)

    const humanError = (e) => e?.response?.data?.detail || e?.message || 'Не получилось загрузить. Повторите через минуту.'

    const loadSections = async () => {
      loading.value = true
      try {
        const data = await fetchKnowledge()
        sections.value = data.sections || []
        level.value = data.level || 'all'
        levelTitle.value = data.level_title || ''
      } catch (e) {
        error.value = humanError(e)
      } finally {
        loading.value = false
      }
    }

    const runSearch = debounce(async (text) => {
      if (!text.trim()) { results.value = []; answer.value = null; searching.value = false; return }
      try {
        const data = await fetchKnowledge(text.trim())
        if (text !== query.value) return
        results.value = data.results || []
        answer.value = data.answer || null
      } catch (e) {
        error.value = humanError(e)
      } finally {
        searching.value = false
      }
    }, 250)

    watch(query, (text) => {
      searching.value = Boolean(text.trim())
      error.value = ''
      runSearch(text)
      router.replace({ query: { ...route.query, q: text.trim() || undefined } }).catch(() => {})
    })

    const ask = (text) => { query.value = text; input.value?.focus() }

    const open = async (slug) => {
      if (!slug) return
      articleLoading.value = true
      article.value = null
      router.replace({ query: { ...route.query, a: slug } }).catch(() => {})
      try {
        article.value = await fetchArticle(slug)
      } catch (e) {
        error.value = humanError(e)
        router.replace({ query: { ...route.query, a: undefined } }).catch(() => {})
      } finally {
        articleLoading.value = false
      }
    }
    const close = () => {
      article.value = null
      articleLoading.value = false
      router.replace({ query: { ...route.query, a: undefined } }).catch(() => {})
    }

    const onKey = (event) => {
      if (event.key === 'Escape' && (article.value || articleLoading.value)) close()
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)
      if (event.key === '/' && !typing) { event.preventDefault(); input.value?.focus() }
    }

    onMounted(() => {
      loadSections()
      if (query.value) { searching.value = true; runSearch(query.value) }
      if (route.query.a) open(String(route.query.a))
      window.addEventListener('keydown', onKey)
      placeholderTimer = setInterval(() => {
        placeholderIndex.value = (placeholderIndex.value + 1) % EXAMPLES.length
      }, 4000)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', onKey)
      clearInterval(placeholderTimer)
      runSearch.cancel()
    })

    return {
      input, sections, level, levelTitle, loading, error, query, results, answer, searching, article,
      articleLoading, placeholder, examples, blocks, sectionTitle, mark, ask, open, close,
    }
  },
}
</script>

<style scoped>
.kb { --gold: #f2b705; --ink: #1f2430; --muted: #6b7280; --line: #e7e5df;
  padding: 24px; max-width: 1180px; color: var(--ink); }
.kb-hero { position: relative; overflow: hidden; padding: 34px 32px 26px; border-radius: 22px; color: #fff;
  background: linear-gradient(135deg, #1f2430 0%, #262c3b 50%, #3a3220 100%); }
.kb-hero__orb { position: absolute; border-radius: 50%; filter: blur(2px); pointer-events: none; }
.kb-hero__orb--a { width: 380px; height: 380px; right: -80px; top: -160px;
  background: radial-gradient(circle, rgba(242,183,5,.5), rgba(242,183,5,0) 65%); animation: kb-float 10s ease-in-out infinite; }
.kb-hero__orb--b { width: 260px; height: 260px; left: 30%; bottom: -170px;
  background: radial-gradient(circle, rgba(99,102,241,.35), rgba(99,102,241,0) 65%); animation: kb-float 13s ease-in-out infinite reverse; }
.kb-eyebrow { position: relative; font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: var(--gold); }
.kb-hero h1 { position: relative; margin: 8px 0 6px; font-size: 30px; letter-spacing: -.01em; }
.kb-level { position: relative; margin: 0 0 18px; color: #cfd2da; font-size: 13px; display: flex; align-items: center; gap: 8px; }
.kb-dot { width: 8px; height: 8px; border-radius: 50%; background: #9ca3af; box-shadow: 0 0 0 4px rgba(255,255,255,.08); }
.kb-dot--leads { background: #60a5fa; }
.kb-dot--commercial { background: var(--gold); }
.kb-search { position: relative; display: flex; align-items: center; gap: 10px; max-width: 760px; height: 54px;
  padding: 0 16px; border-radius: 16px; background: #fff; color: var(--ink);
  box-shadow: 0 18px 40px rgba(0,0,0,.25); transition: box-shadow .25s, transform .25s; }
.kb-search:focus-within { box-shadow: 0 0 0 4px rgba(242,183,5,.45), 0 18px 40px rgba(0,0,0,.25); transform: translateY(-1px); }
.kb-search--busy::after { content: ''; position: absolute; left: 16px; right: 16px; bottom: 6px; height: 2px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent); background-size: 200% 100%;
  animation: kb-shimmer 1s linear infinite; }
.kb-search input { flex: 1; border: 0; outline: 0; font-size: 16px; background: transparent; }
.kb-search__icon { font-size: 20px; color: var(--muted); }
.kb-search kbd { font-size: 12px; color: var(--muted); border: 1px solid var(--line); border-radius: 6px; padding: 1px 6px; }
.kb-chips { position: relative; display: flex; gap: 8px; flex-wrap: wrap; margin-top: 14px; }
.kb-chip { border: 1px solid rgba(255,255,255,.18); background: rgba(255,255,255,.06); color: #e5e7eb;
  border-radius: 999px; padding: 6px 12px; font-size: 12px; cursor: pointer; transition: all .2s; }
.kb-chip:hover { background: rgba(242,183,5,.18); border-color: var(--gold); color: #fff; }
.kb-error { color: #b91c1c; margin: 14px 0 0; }
.kb-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(330px, 1fr)); gap: 14px; margin-top: 20px; }
.kb-section { background: #fff; border: 1px solid var(--line); border-radius: 18px; padding: 16px 16px 10px;
  animation: kb-rise .45s ease both; }
.kb-section h2 { font-size: 15px; margin: 0 0 8px; display: flex; gap: 10px; align-items: center; }
.kb-section__icon { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 10px;
  background: linear-gradient(180deg, #fffaf0, #fff0c2); font-size: 18px; }
.kb-link { position: relative; display: flex; flex-direction: column; gap: 2px; width: 100%; text-align: left;
  border: 0; background: none; padding: 9px 10px; border-radius: 12px; cursor: pointer; color: var(--ink);
  transition: background .2s, transform .2s; }
.kb-link:hover { background: #fbf8ef; transform: translateX(3px); }
.kb-link span { font-weight: 600; font-size: 14px; }
.kb-link small { color: var(--muted); font-size: 12px; line-height: 1.4; }
.kb-link--small span { font-weight: 500; }
.kb-badge { display: inline-block; width: fit-content; margin-top: 4px; font-style: normal; font-size: 10px;
  padding: 2px 8px; border-radius: 999px; }
.kb-badge--leads { background: #dbeafe; color: #1e40af; }
.kb-badge--commercial { background: #fef3c7; color: #92400e; }
.kb-results { margin-top: 20px; }
.kb-answer { cursor: pointer; padding: 18px 20px; border-radius: 18px; color: #fff; margin-bottom: 14px;
  background: linear-gradient(135deg, #2b3142, #3b3420); box-shadow: 0 14px 36px rgba(31,36,48,.18);
  animation: kb-rise .35s ease both; }
.kb-answer__label { font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: var(--gold); }
.kb-answer__text { font-size: 17px; line-height: 1.5; margin: 6px 0 8px; }
.kb-answer__more { font-size: 13px; color: #e5d39a; }
.kb-answer mark { background: rgba(242,183,5,.35); color: #fff; border-radius: 3px; padding: 0 2px; }
.kb-list { display: flex; flex-direction: column; gap: 8px; }
.kb-item { display: flex; flex-direction: column; gap: 4px; text-align: left; padding: 14px 16px; border-radius: 14px;
  border: 1px solid var(--line); background: #fff; cursor: pointer; transition: transform .2s, box-shadow .2s; }
.kb-item:hover { transform: translateY(-2px); box-shadow: 0 10px 26px rgba(31,36,48,.08); }
.kb-item__title { font-weight: 650; }
.kb-item__text { color: #374151; line-height: 1.45; font-size: 14px; }
.kb-item__meta { font-size: 12px; color: var(--muted); }
.kb-item mark { background: #fff0c2; color: inherit; border-radius: 3px; padding: 0 1px; }
.kb-empty { color: var(--muted); padding: 20px 0; }
.kb-overlay { position: fixed; inset: 0; z-index: 50; background: rgba(17,20,28,.42); backdrop-filter: blur(3px);
  display: flex; justify-content: flex-end; }
.kb-reader { position: relative; width: min(640px, 100%); height: 100%; overflow-y: auto; background: #fff;
  padding: 34px 34px 40px; box-shadow: -20px 0 50px rgba(0,0,0,.18); line-height: 1.6; }
.kb-reader h2 { font-size: 24px; margin: 6px 0 6px; line-height: 1.25; }
.kb-reader__section { font-size: 12px; color: var(--muted); text-transform: uppercase; letter-spacing: .08em; }
.kb-reader__lead { font-size: 16px; color: #374151; padding: 12px 14px; border-radius: 12px; background: #fbf8ef;
  border-left: 3px solid var(--gold); }
.kb-reader ul { padding-left: 20px; }
.kb-reader li { margin: 4px 0; }
.kb-related { margin-top: 24px; padding-top: 14px; border-top: 1px solid var(--line); }
.kb-related h3 { font-size: 13px; color: var(--muted); margin: 0 0 6px; text-transform: uppercase; letter-spacing: .06em; }
.kb-close { position: absolute; top: 14px; right: 16px; width: 36px; height: 36px; border-radius: 50%;
  border: 1px solid var(--line); background: #fff; font-size: 20px; cursor: pointer; transition: transform .2s; }
.kb-close:hover { transform: rotate(90deg); }
.kb-muted { color: var(--muted); }
.kb-skeleton { display: contents; }
.kb-skeleton__card { height: 210px; border-radius: 18px; background: linear-gradient(90deg, #f1f1ef 25%, #fafaf8 50%, #f1f1ef 75%);
  background-size: 200% 100%; animation: kb-shimmer 1.2s infinite; }
.kb-fade-enter-active, .kb-fade-leave-active { transition: opacity .2s ease; }
.kb-fade-enter-from, .kb-fade-leave-to { opacity: 0; }
.kb-list-enter-active, .kb-list-leave-active { transition: all .22s ease; }
.kb-list-enter-from, .kb-list-leave-to { opacity: 0; transform: translateY(6px); }
.kb-drawer-enter-active, .kb-drawer-leave-active { transition: opacity .25s ease; }
.kb-drawer-enter-active .kb-reader, .kb-drawer-leave-active .kb-reader { transition: transform .3s cubic-bezier(.2,.8,.2,1); }
.kb-drawer-enter-from, .kb-drawer-leave-to { opacity: 0; }
.kb-drawer-enter-from .kb-reader, .kb-drawer-leave-to .kb-reader { transform: translateX(40px); }
@keyframes kb-float { 50% { transform: translate(-30px, 24px); } }
@keyframes kb-shimmer { to { background-position: -200% 0; } }
@keyframes kb-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .kb-hero__orb, .kb-section, .kb-answer, .kb-skeleton__card { animation: none; }
  .kb-link, .kb-item, .kb-chip, .kb-search { transition: none; }
}
@media (max-width: 760px) {
  .kb { padding: 16px; }
  .kb-hero { padding: 24px 18px 20px; }
  .kb-hero h1 { font-size: 23px; }
  .kb-reader { padding: 26px 18px 32px; }
  .kb-grid { grid-template-columns: 1fr; }
}
</style>
